import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Clock3, CalendarDays, Flame, Link2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../assets/logo.png';
import { trackUpgradeClick } from '../../utils/analytics';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: () => void;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, onSubscribe }) => {
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [isOpen]);

  const compactFeatures = [
    'Real-time data (no delay)',
    '30 days of history',
    'Spike tab + full WSS analysis',
    'Price Linkage insights',
    'Detailed intervals: 5m, 15m, 30m, 1h, 1d',
  ];

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop — блокирует скролл и клики по странице */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="upgrade-modal-backdrop fixed inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            onWheel={(e) => e.preventDefault()}
          />

          {/* Modal — выше хедера, вписан по высоте экрана */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="upgrade-modal-wrap fixed inset-0 flex items-center justify-center p-4 sm:p-6"
          >
            <div
              className="upgrade-modal-panel bg-brand-card border border-brand-border rounded-2xl max-w-2xl w-full overflow-hidden flex flex-col shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header — лого и золотой акцент */}
              <div className="shrink-0 bg-brand-card border-b border-brand-border p-4 sm:p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src={logo} alt="SentiHype" className="w-12 h-12 object-contain" />
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white">
                      Upgrade to <span className="text-brand-gold">Premium</span>
                    </h2>
                    <p className="text-white/70 text-sm">Unlock all features</p>
                    <p className="text-[11px] text-brand-gold/80 font-semibold mt-1">Most popular among active traders</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition text-white/70 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 sm:p-5 space-y-4">
                <div className="upgrade-modal-benefits-row">
                  <div className="upgrade-modal-benefit-pill">
                    <Clock3 className="w-4 h-4" />
                    <span>0h delay</span>
                  </div>
                  <div className="upgrade-modal-benefit-pill">
                    <CalendarDays className="w-4 h-4" />
                    <span>30d history</span>
                  </div>
                  <div className="upgrade-modal-benefit-pill">
                    <Flame className="w-4 h-4" />
                    <span>Spike tab</span>
                  </div>
                  <div className="upgrade-modal-benefit-pill">
                    <Link2 className="w-4 h-4" />
                    <span>Price Linkage</span>
                  </div>
                </div>

                {/* Pricing — золотой блок */}
                <div
                  className="upgrade-modal-pricing rounded-xl p-6 border"
                >
                  <div className="text-center">
                    <div className="text-4xl font-bold text-white mb-2">
                      $19.99<span className="text-lg text-white/60">/month</span>
                    </div>
                    <p className="text-white/60 mb-3">Cancel anytime</p>
                    <p className="text-sm text-white/80">
                      Includes <span className="text-brand-gold font-medium">Spike tab</span> and <span className="text-brand-gold font-medium">Price Linkage</span> on Dashboard/Ticker.
                    </p>
                  </div>
                </div>

                <div className="upgrade-modal-compact-features">
                  {compactFeatures.map((feature, index) => (
                    <div key={index} className="upgrade-modal-compact-feature">
                      <Check className="w-4 h-4 text-brand-gold flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-white/85">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="upgrade-modal-cta-wrap flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      trackUpgradeClick('upgrade_modal', 'subscribe_click');
                      onSubscribe();
                    }}
                    className="btn btn-gold flex-1"
                  >
                    Subscribe to Premium
                  </button>
                  <button
                    onClick={onClose}
                    className="btn btn-outline"
                  >
                    Maybe later
                  </button>
                </div>
                <p className="upgrade-modal-trust-note">Secure checkout via Stripe • Cancel anytime</p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default UpgradeModal;
